import React, { useState, useEffect } from "react";
import {
  Eye,
  ArrowLeft,
  Plus,
  CirclePlus,
  Trash2,
  Search,
  Pencil,
  ChevronLeft,
  CircleAlert,
} from "lucide-react";
import { data, useNavigate } from "react-router-dom";
import "./activeclient.css";
import { BASE_URL } from "../apiConfig";
import { message } from "antd";
import UpdateIcon from "@mui/icons-material/Update";

const ClientManagement = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [newClient, setNewClient] = useState({
    id: "",
    name: "",
    address1: "",
    address2: "",
    logo: null,
    logoUrl: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    address1: "",
    address2: "",
    logo: "",
  });

  const [editClient, setEditClient] = useState({
    client_id: "",
    name: "",
    address1: "",
    address2: "",
    logo: null,
    logoUrl: "",
  });
  const [previewLogo, setPreviewLogo] = useState(null);
  const [editPreviewLogo, setEditPreviewLogo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch clients data when component mounts
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    const token = localStorage.getItem("accessToken");

    try {
      const response = await fetch(`${BASE_URL}/client/client_list`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch clients");
      }

      const data = await response.json();
      setClients(data.reverse());
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const openModal = () => {
    setIsModalOpen(true);
    // Reset form and errors when opening modal
    setNewClient({
      client_id: "",
      name: "",
      address1: "",
      address2: "",
      logo: null,
      logoUrl: "",
    });
    setErrors({
      name: "",
      address1: "",
      address2: "",
      logo: "",
    });
    setPreviewLogo(null);
  };

  const openEditModal = (clientId) => {
    try {
      // Find the client from the existing clients array in state
      const clientData = clients.find((client) => client.id === clientId);

      if (!clientData) {
        throw new Error("Client not found");
      }

      console.log("Client details:", clientData);

      // Set the form data with the client information
      setEditClient({
        client_id: clientData.id || clientId,
        name: clientData.name || "",
        address1: clientData.address_1 || "",
        address2: clientData.address_2 || "",
        logo: null,
        logoUrl: clientData.image || "", // Make sure this is correctly set
      });

      // Make sure the preview is always set if an image exists
      if (clientData.image) {
        setEditPreviewLogo(clientData.image);
      } else {
        setEditPreviewLogo(null);
      }

      setUploadedImageUrl(null); // Reset any previously uploaded image URL
      setErrors({});

      setIsEditModalOpen(true);
    } catch (error) {
      console.error("Error opening edit modal:", error);
      alert("Failed to load client details. Please try again.");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditModalOpen(false);
    setNewClient({
      id: "",
      name: "",
      address1: "",
      address2: "",
      logo: null,
      logoUrl: "",
    });
    setEditClient({
      client_id: "",
      name: "",
      address1: "",
      address2: "",
      logo: null,
      logoUrl: "",
    });
    setPreviewLogo(null);
    setEditPreviewLogo(null);
    setErrors({
      name: "",
      address1: "",
      address2: "",
      logo: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClient({
      ...newClient,
      [name]: value,
    });

    // Clear error for this field when user starts typing
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditClient({
      ...editClient,
      [name]: value,
    });

    // Clear error for this field when user starts typing
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        logo: "Please select a valid image file.",
      }));
      return;
    }

    // Allowed file formats
    const allowedFormats = ["image/jpeg", "image/png"];
    if (!allowedFormats.includes(file.type)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        logo: "Invalid file format. Only JPEG and PNG are allowed.",
      }));
      return;
    }

    // Store the file for preview only
    setNewClient((prevClient) => ({
      ...prevClient,
      logo: file,
    }));

    // Create URL for preview only
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewLogo(reader.result);
    };
    reader.readAsDataURL(file);

    setErrors((prevErrors) => ({
      ...prevErrors,
      logo: "",
    }));

    // Upload the image file immediately after selection
    await uploadLogo(file, false);
  };

  const handleEditLogoChange = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        logo: "Please select a valid image file.",
      }));
      return;
    }

    // Allowed file formats
    const allowedFormats = ["image/jpeg", "image/png"];
    if (!allowedFormats.includes(file.type)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        logo: "Invalid file format. Only JPEG and PNG are allowed.",
      }));
      return;
    }

    setEditClient((prevClient) => ({
      ...prevClient,
      logo: file,
    }));

    const reader = new FileReader();
    reader.onloadend = () => {
      setEditPreviewLogo(reader.result);
    };
    reader.readAsDataURL(file);

    setErrors((prevErrors) => ({
      ...prevErrors,
      logo: "",
    }));

    await uploadLogo(file, true);
  };

  const uploadLogo = async (file, isEdit) => {
    setIsUploading(true);

    try {
      const token = localStorage.getItem("accessToken");

      const myHeaders = new Headers();

      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const formData = new FormData();
      formData.append("image_logo", file); // Using the same field name as in the Postman snippet

      // Request options based on the Postman snippet
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formData,
        redirect: "follow",
      };

      // Send the request to the upload endpoint with the document_type query parameter
      const response = await fetch(
        `${BASE_URL}/upload_picture?document_type=client`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      // Parse the response
      const result = await response.json();

      // Store the returned image URL in state
      if (isEdit) {
        setEditClient((prevClient) => ({
          ...prevClient,
          logoUrl: result.filename, // Update this to match your API response structure
        }));
      } else {
        setNewClient((prevClient) => ({
          ...prevClient,
          logoUrl: result.filename, // Update this to match your API response structure
        }));
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      setErrors((prevErrors) => ({
        ...prevErrors,
        logo: "Failed to upload image. Please try again.",
      }));
    } finally {
      setIsUploading(false);
    }
  };

  const validateForm = (client) => {
    let isValid = true;
    const newErrors = {
      name: "",
      address1: "",
      address2: "",
      logo: "",
    };

    if (!client.name.trim()) {
      newErrors.name = "This field cannot be empty!";
      isValid = false;
    } else if (client.name.trim().length < 2) {
      newErrors.name = "Minimum Character Limit not met,please contact Admin!";
      isValid = false;
    } else if (client.name.trim().length > 40) {
      newErrors.name =
        "Maximum Character Limit Exceeded,please recheck or contact Admin!";
      isValid = false;
    }

    if (!client.address1.trim()) {
      newErrors.address1 = "This field cannot be empty!";
      isValid = false;
    }

    if (!client.address2.trim()) {
      newErrors.address2 = "This field cannot be empty!";
      isValid = false;
    }

    // For edit form, we don't require a new logo if one already exists (logoUrl)
    if (!client.logoUrl) {
      newErrors.logo = "Client logo is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm(newClient)) {
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");

      const requestBody = {
        name: newClient.name,
        address_1: newClient.address1,
        address_2: newClient.address2,
        image: newClient.logoUrl, // Now this is the URL from the image upload API
      };

      // Make POST request to create client
      const response = await fetch(`${BASE_URL}/client/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        messageApi.error(data.detail);
      } else {
        fetchClients();
        closeModal();
        messageApi.success("Client created successfully!");
      }
    } catch (error) {
      console.error("Error creating client:", error);
      if (error.status == 409) {
        alert("Duplicate entry, please enter another name");
        return;
      }
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm(editClient)) {
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");

      // Make sure we're using the correct image URL
      // If no new logo was uploaded, use the existing URL from editPreviewLogo
      // const imageUrl = editClient.logoUrl || editPreviewLogo;
      // console.log("edit", editPreviewLogo);

      let imageUrl;
      // if (editClient.logo) {
      //   // If a new file was uploaded, use the URL from the upload process
      //   imageUrl = editClient.logoUrl;
      // } else {
      //   // Otherwise use the original URL from the server
      //   imageUrl = originalLogoUrl;
      // }
      imageUrl = editClient.logo;

      const requestBody = {
        client_id: editClient.client_id,
        name: editClient.name,
        address_1: editClient.address1,
        address_2: editClient.address2,
        image: imageUrl, // Use the determined image URL
      };

      console.log("Update request payload:", requestBody);

      // Make POST request to update client
      const response = await fetch(`${BASE_URL}/client/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        messageApi.error(data.detail);
      } else {
        fetchClients();
        closeModal();
        messageApi.success("Client updated successfully!");
      }
    } catch (error) {
      console.error("Error updating client:", error);
    }
  };

  const handleViewRole = (clientId) => {
    navigate(`/dashboard/${clientId}/roles`);
  };

  // New functions for delete functionality
  const confirmDeleteRole = async () => {
    setClientToDelete(editClient.client_id);

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Unauthorized access. Please log in again.");
      }
      const response = await fetch(
        `${BASE_URL}/client/role_count_under_client?client_id=${editClient.client_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error("Failed to check role deletion");
      }
      if (data === 0) {
        setShowDeleteModal(true);
      } else {
        setShowWarningModal(true);
        // messageApi.error("Role cannot be deleted until all associated Projects are deleted.");
      }
    } catch (error) {
      console.error("Error checking role deletion:", error);
      messageApi.error("Failed to check role deletion");
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setClientToDelete(null);
  };

  const confirmDeleteClient = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      // Make GET request to delete client using the provided API endpoint
      const response = await fetch(
        `${BASE_URL}/client/delete_client?client_id=${clientToDelete}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Failed to delete client. Status: ${response.status}. ${
            errorData.message || ""
          }`
        );
      }

      // Close the delete modal
      setShowDeleteModal(false);

      // Close the edit modal
      setIsEditModalOpen(false);

      // Refresh the client list to remove the deleted client
      fetchClients();

      // alert("Client deleted successfully!");
      messageApi.success("Client deleted successfully!");
    } catch (error) {
      console.error("Error deleting client:", error);
      messageApi.error("Failed to delete client. Please try again.");
    } finally {
      setClientToDelete(null);
    }
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {contextHolder}
      {/* Placeholder for the sidebar - in a real app, this would be a separate component */}
      <div className="client-sidebar"></div>

      <div className="client-management">
        <div className="client-management-inner">
          <div className="client-header">
            <h1 className="client-heading">Active Clients</h1>
            <button className="client-add-btn" onClick={openModal}>
              {/* <span className="client-plus-icon">
                <CirclePlus size={16} />
              </span> */}
              Add New Client
            </button>
          </div>

          <div className="client-search-container">
            <input
              type="text"
              placeholder="Search Client...."
              value={searchTerm}
              onChange={handleSearchChange}
              className="client-search-input"
            />
            <button className="client-search-btn">
              <span className="client-search-icon">
                <Search size={16} />
              </span>
            </button>
          </div>

          <div className="client-grid">
            {filteredClients.map((client) => (
              <div key={client.id} className="client-card">
                <div className="client-info">
                  <h2>{client.name}</h2>
                  <div className="client-logo">
                    <img
                      src={client.image}
                      alt={`${client.name} logo`}
                      onError={(e) => {
                        // Fallback for broken images
                        e.target.src = "/default-logo.png"; // Use a default image
                      }}
                    />
                  </div>
                </div>
                <div className="client-actions">
                  <button
                    className="client-edit-btn"
                    onClick={() => openEditModal(client.id)}
                  >
                    <span className="client-edit-icon">
                      <Pencil size={14} />
                    </span>{" "}
                    Edit
                  </button>
                  <button
                    className="client-view-role-btn"
                    onClick={() => handleViewRole(client.id)}
                  >
                    <span className="client-eye-icon">
                      <Eye size={18} />
                    </span>{" "}
                    View Role
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add New Client Modal */}
      {isModalOpen && (
        <div className="client-modal-overlay">
          <div className="client-modal-container">
            <div className="client-modal-header">
              <button className="client-back-button" onClick={closeModal}>
                <ChevronLeft size={20} />
                <span className="client-header-title">Create Client</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="client-form">
              <div className="client-form-main-layout">
                {/* Name and Logo in the same row */}
                <div className="client-name-logo-row">
                  <div className="client-name-field-container">
                    <div className="client-name-form-group">
                      <label htmlFor="name" className="client-name-label">
                        Client's Name
                        <span className="client-name-required-asterisk">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="client-name-input"
                        value={newClient.name}
                        onChange={handleInputChange}
                        placeholder="Enter Client's Name"
                      />
                      {errors.name && (
                        <div
                          className="error-message"
                          style={{ fontSize: "12px" }}
                        >
                          {errors.name}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="client-logo-upload-section">
                    <input
                      type="file"
                      id="client-logo-input"
                      accept="image/jpeg,image/png"
                      onChange={handleLogoChange}
                      className="client-logo-file-input"
                      style={{ display: "none" }}
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="client-logo-input"
                      className="client-logo-upload-label"
                    >
                      {previewLogo ? (
                        <div className="client-logo-preview-container">
                          <img
                            src={previewLogo}
                            alt="Logo preview"
                            className="client-logo-image-preview"
                          />
                          {isUploading && (
                            <div className="client-logo-upload-overlay">
                              Uploading...
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="client-logo-upload-placeholder">
                          <Plus size={24} className="client-logo-plus-icon" />
                          <div className="client-logo-upload-text">
                            <span className="client-logo-add-text">Add</span>
                            <span className="client-logo-logo-text">Logo</span>
                          </div>
                          {isUploading && (
                            <div className="client-logo-uploading">
                              Uploading...
                            </div>
                          )}
                        </div>
                      )}
                    </label>
                    {errors.logo && (
                      <div
                        className="error-message"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.logo}
                      </div>
                    )}
                    <div className="client-logo-file-info-container">
                      <p className="client-logo-file-supported-text">
                        File supported:
                      </p>
                      <p className="client-logo-file-format-text">
                        jpg, jpeg or png <span style={{ color: "red" }}>*</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Address fields - full width */}
                <div className="client-address-fields-container">
                  <div className="client-address-main-group">
                    <label
                      htmlFor="address1"
                      className="client-address-main-label"
                    >
                      Client's Office Address
                      <span className="client-address-required-asterisk">
                        *
                      </span>
                    </label>

                    <p className="client-address-line1-label">Address Line 1</p>
                    <input
                      type="text"
                      id="address1"
                      name="address1"
                      className="client-address-line1-input"
                      value={newClient.address1}
                      onChange={handleInputChange}
                      placeholder="Enter Address Line 1"
                    />
                    {errors.address1 && (
                      <div
                        className="error-message"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.address1}
                      </div>
                    )}
                  </div>

                  <div className="client-address-line2-group">
                    <p className="client-address-line2-label">Address Line 2</p>
                    <input
                      type="text"
                      id="address2"
                      name="address2"
                      className="client-address-line2-input"
                      value={newClient.address2}
                      onChange={handleInputChange}
                      placeholder="Enter Address Line 2"
                    />
                    {errors.address2 && (
                      <div
                        className="error-message"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.address2}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="client-form-action-buttons">
                <button
                  type="button"
                  className="client-discard-button"
                  onClick={closeModal}
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="client-create-button"
                  disabled={isUploading}
                >
                  Create{" "}
                  <span className="client-create-info-icon">
                    {" "}
                    {/* <CirclePlus size="16px" />{" "} */}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {isEditModalOpen && (
        <div className="client-modal-overlay">
          <div className="client-modal-container">
            <div className="client-modal-header">
              <button className="client-back-button" onClick={closeModal}>
                <ChevronLeft size={20} />
                <span className="client-header-title">Edit Client</span>
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="client-form">
              <div className="client-form-main-layout">
                {/* Name and Logo in the same row */}
                <div className="client-name-logo-row">
                  <div className="client-name-field-container">
                    <div className="client-name-form-group">
                      <label htmlFor="edit-name" className="client-name-label">
                        Client's Name
                        <span className="client-name-required-asterisk">*</span>
                      </label>
                      <input
                        type="text"
                        id="edit-name"
                        name="name"
                        className="client-name-input"
                        value={editClient.name}
                        onChange={handleEditInputChange}
                      />
                      {errors.name && (
                        <div
                          className="error-message"
                          style={{ fontSize: "12px" }}
                        >
                          {errors.name}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="client-logo-upload-section">
                    <input
                      type="file"
                      id="edit-client-logo-input"
                      accept="image/jpeg,image/png"
                      onChange={handleEditLogoChange}
                      className="client-logo-file-input"
                      style={{ display: "none" }}
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="edit-client-logo-input"
                      className="client-logo-upload-label"
                    >
                      {editPreviewLogo ? (
                        <div className="client-logo-preview-container">
                          <img
                            src={editPreviewLogo}
                            alt="Logo preview"
                            className="client-logo-image-preview"
                          />
                          {isUploading && (
                            <div className="client-logo-upload-overlay">
                              Uploading...
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="client-logo-upload-placeholder">
                          <Plus size={24} className="client-logo-plus-icon" />
                          <div className="client-logo-upload-text">
                            <span className="client-logo-add-text">Add</span>
                            <span className="client-logo-logo-text">Logo</span>
                          </div>
                          {isUploading && (
                            <div className="client-logo-uploading">
                              Uploading...
                            </div>
                          )}
                        </div>
                      )}
                    </label>
                    {errors.logo && (
                      <div
                        className="error-message"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.logo}
                      </div>
                    )}
                    <div className="client-logo-file-info-container">
                      <p className="client-logo-file-supported-text">
                        File supported:
                      </p>
                      <p className="client-logo-file-format-text">
                        jpg, jpeg or png
                      </p>
                    </div>
                  </div>
                </div>

                {/* Address fields - full width */}
                <div className="client-address-fields-container">
                  <div className="client-address-main-group">
                    <label
                      htmlFor="edit-address1"
                      className="client-address-main-label"
                    >
                      Client's Office Address
                      <span className="client-address-required-asterisk">
                        *
                      </span>
                    </label>

                    <p className="client-address-line1-label">Address Line 1</p>
                    <input
                      type="text"
                      id="edit-address1"
                      name="address1"
                      className="client-address-line1-input"
                      value={editClient.address1}
                      onChange={handleEditInputChange}
                      placeholder="Enter Address Line 1"
                    />
                    {errors.address1 && (
                      <div
                        className="error-message"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.address1}
                      </div>
                    )}
                  </div>

                  <div className="client-address-line2-group">
                    <p className="client-address-line2-label">Address Line 2</p>
                    <input
                      type="text"
                      id="edit-address2"
                      name="address2"
                      className="client-address-line2-input"
                      value={editClient.address2}
                      onChange={handleEditInputChange}
                      placeholder="Enter Address Line 2"
                    />
                    {errors.address2 && (
                      <div
                        className="error-message"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.address2}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="client-form-edit-action-buttons">
                <div className="client-first-div">
                  <button
                    type="button"
                    className="client-delete-button"
                    onClick={confirmDeleteRole}
                  >
                    <Trash2 size={16} />
                    Delete Client
                  </button>
                </div>

                <div className="client-second-div">
                  <button
                    type="button"
                    className="client-discard-button"
                    onClick={closeModal}
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    className="client-create-button"
                    disabled={isUploading}
                  >
                    Update{" "}
                    {/* <span className="client-create-info-icon">
                      <UpdateIcon sx={{ fontSize: "17px", marginTop: "4px" }} />
                    </span> */}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="role-delete-modal-overlay">
          <div className="role-delete-modal-container">
            <div className="role-delete-modal-content">
              <h3>Confirm Delete Client?</h3>
              <div className="role-delete-modal-buttons">
                <button
                  className="role-delete-modal-button role-delete-cancel"
                  onClick={cancelDelete}
                >
                  No
                </button>
                <button
                  className="role-delete-modal-button role-delete-confirm"
                  onClick={confirmDeleteClient}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWarningModal && (
        <div className="warning-modal-overlay">
          <div className="warning-modal-container">
            <button
              className="warning-modal-close"
              onClick={() => setShowWarningModal(false)}
            >
              ×
            </button>
            <h3 className="warning-modal-text">
              <CircleAlert size={20} />
              Client cannot be deleted until all associated Roles are deleted.
            </h3>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientManagement;
